import { initializeTagsPanel } from "@modules/bookmarks/advert-page/tags-panel"
import { bookmarkIdOf, currentBookmarkId } from "@modules/bookmarks/bookmark-identity.ts"
import { updateMerkzettelStateInMenu } from "@modules/bookmarks/bookmarks-menu-item.ts"
import { registerTagListUpdatedCallback } from "@modules/bookmarks/tags/tags-dropdown"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import bookmarkTags, { type HTMLTagsListElement, registerBookmarkTagsEvents } from "./bookmark-tags"
import { deleteBookmarkEntryModal } from "./modal-selectors"

export type BookmarksApiResponse = {
    status: string | false
    count: number
    countMax?: number
}

type EntryRequestResponse = { ok: boolean; message?: string }
type Listener = (state: BookmarkState) => void
type BookmarkState = {
    bookmarked: boolean
    showUpdateMessage: boolean
}

function createBookmarkStore(initial: BookmarkState) {
    let state: BookmarkState = { ...initial }
    const listeners = new Set<Listener>()

    return {
        getState: (): BookmarkState => state,
        updateState: (newState: BookmarkState): void => {
            state = newState
            for (const fn of listeners) {
                fn({ ...state })
            }
        },
        subscribe: (listenerCallback: Listener) => {
            listeners.add(listenerCallback)
            return () => listeners.delete(listenerCallback)
        },
    }
}

export const bookmarkStore = createBookmarkStore({ bookmarked: false, showUpdateMessage: false })

const apiBaseUrl: string = SiteInfo.yiiLadiesUrl
const apiEndpoint = `${apiBaseUrl}/merkzettel/AjaxUpdate`
const advertId = SiteInfo.advertId
const canonicalId = SiteInfo.canonicalId
// The id bookmark state is keyed by on this page: canonical id with the new
// user API active, advert id with the legacy Yii API. See bookmark-identity.ts.
const bookmarkId = currentBookmarkId()
const requestData = {
    id: Number(advertId),
    canonicalId,
}

const bookmarkButtons = (): Array<HTMLElement> => [
    ...(document.querySelectorAll(".merkzettel-button") as NodeListOf<HTMLElement>),
]
const counterElement = (): HTMLElement | null =>
    document.querySelector(".merkzettel-counter.badge") as HTMLElement | null

let bookmarkButtonList: Array<HTMLElement> = []
let deleteEntryModal: HTMLElement | null = null

function setTagAddAdvertToBookmarkCallback() {
    registerTagListUpdatedCallback(async (): Promise<void> => {
        const response = await manageEntries([requestData], "add")

        if (response.ok) {
            bookmarkStore.updateState({
                bookmarked: true,
                showUpdateMessage: true,
            })

            registerTagListUpdatedCallback(null)
        }
    })
}

async function addEventListenerToBookmarkButtons() {
    if (!advertId) {
        return
    }

    const bookmarkButtons: NodeListOf<HTMLButtonElement> =
        document.querySelectorAll<HTMLButtonElement>(".merkzettel-button")

    if (bookmarkButtons.length === 0) {
        return
    }

    bookmarkStore.subscribe((newState: BookmarkState): void => {
        if (!newState.showUpdateMessage) {
            return
        }

        displayUpdateInfo(
            newState.bookmarked
                ? "Die Anzeige wurde erfolgreich zum Merkzettel hinzugefügt."
                : "Die Anzeige wurde erfolgreich aus dem Merkzettel entfernt.",
        )
    })

    for (const button of bookmarkButtons) {
        bookmarkStore.subscribe((state: BookmarkState): void => {
            button.classList.add(state.bookmarked ? "remove" : "add")
            button.classList.remove(state.bookmarked ? "add" : "remove")
        })

        button.classList.remove("add", "remove")
        button.classList.add(bookmarkStore.getState().bookmarked ? "remove" : "add")

        button.addEventListener("click", async (): Promise<void> => {
            let response: EntryRequestResponse
            registerTagListUpdatedCallback(null)

            if (bookmarkStore.getState().bookmarked) {
                response = await manageEntries([requestData], "delete")

                if (response.ok) {
                    ;(document.body as HTMLTagsListElement).tagsList = []
                    setTagAddAdvertToBookmarkCallback()
                }
            } else {
                response = await manageEntries([requestData], "add")
            }

            if (response.ok) {
                bookmarkStore.updateState({
                    bookmarked: !bookmarkStore.getState().bookmarked,
                    showUpdateMessage: true,
                })
            } else {
                InfoPanel.displayMessage(response.message as string, InfoPanel.Type.Error, 2 * 1000)
            }
        })
    }
}

async function fetchBookmarksList(): Promise<string[]> {
    const api = userData.userId === 0 ? "/api/bookmarks/list" : `/api/bookmarks/${userData.userId}/list`
    let response: Response
    try {
        response = await fetch(api, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
        })
    } catch (error) {
        return []
    }

    if (!response.ok) {
        return []
    }

    const data = await response.json()
    const bookmarkIds: string[] = []
    for (const bookmark of data.entries || []) {
        bookmarkIds.push(bookmarkIdOf(bookmark))
    }

    return bookmarkIds
}

async function personalizeBookmarkButtons(): Promise<void> {
    const bookmarkList: string[] = await fetchBookmarksList()
    const bookmarkButtons: NodeListOf<HTMLButtonElement> =
        document.querySelectorAll<HTMLButtonElement>(".merkzettel-button")

    if (bookmarkButtons.length === 0) {
        return
    }

    bookmarkStore.updateState({
        bookmarked: bookmarkList.includes(bookmarkId),
        showUpdateMessage: false,
    })

    if (!bookmarkStore.getState().bookmarked) {
        setTagAddAdvertToBookmarkCallback()
    }
}

async function manageEntries(
    adverts: { id: number; canonicalId: string }[],
    action: "add" | "delete",
): Promise<EntryRequestResponse> {
    let api: string
    if (userData.userId === 0) {
        api = `/api/bookmarks/${action}`
    } else {
        api = `/api/bookmarks/${userData.userId}/${action}`
    }

    let data: { ids: { advertId: number; canonicalId: string }[] } | { canonicalId: string; confirm: true }
    if (action === "add") {
        data = {
            ids: adverts.map((advert) => ({
                advertId: advert.id,
                canonicalId: advert.canonicalId,
            })),
        }
    } else {
        data = {
            canonicalId: adverts[0].canonicalId,
            confirm: true,
        }
    }

    try {
        const response = await fetch(api, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(data),
        })

        let json: any = null
        try {
            json = await response.json()
        } catch (err) {}

        if (json.maintenanceMode) {
            return {
                ok: false,
                message:
                    "Die Merkzettel-Funktion ist derzeit aufgrund von Wartungsarbeiten nicht verfügbar. Bitte versuche es in einigen Minuten noch einmal.",
            }
        }

        await updateMerkzettelStateInMenu(true)

        return {
            ok: response.ok,
            message: json?.message || undefined,
        }
    } catch (error) {
        return { ok: false, message: "Network error" }
    }
}

export function initBookmarks(): void {
    updateBookmarkButtons()

    if (SiteInfo.bookmarksPageActive) {
        Promise.all([
            registerBookmarkTagsEvents(),
            personalizeBookmarkButtons(),
            addEventListenerToBookmarkButtons(),
        ]).then(() => {
            initializeTagsPanel()
        })
        return
    }

    registerBookmarkButtonEvents()
    updateBookmarkButtonStates()

    bookmarkTags.registerTagPopupEvents()
    bookmarkTags.registerTagPopupRetargetEvents()
}

export function getAdvertId(): string {
    return advertId
}

function updateBookmarkButtonStates(): void {
    if (advertId === "" || !bookmarkButtonList.length) {
        return
    }

    let newAction = "insert"
    let newClass = "add"
    let oldClass = "remove"

    const isAdvertInBookmarksList: boolean = siteConfig.merkzettelIds.indexOf(bookmarkId) >= 0
    if (isAdvertInBookmarksList) {
        newAction = "remove"
        newClass = "remove"
        oldClass = "add"
    }

    requestAnimationFrame((): void => {
        for (const button of bookmarkButtonList) {
            button.dataset.action = newAction
            button.classList.add(newClass)
            button.classList.remove(oldClass)
        }
    })
}

function registerBookmarkButtonEvents(): void {
    for (const button of bookmarkButtonList) {
        button.addEventListener("click", updateBookmarkState, { passive: true })
    }
}

async function updateBookmarkState(clickEvent: MouseEvent): Promise<void> {
    if (advertId === "") {
        return
    }

    const eventTarget: HTMLElement | null = clickEvent.target as HTMLElement | null
    const action: string = eventTarget?.closest("button")?.dataset.action || ""

    if (action === "remove" && siteConfig.merkzettelIdsWithData.indexOf(bookmarkId) >= 0) {
        if (deleteEntryModal === null) {
            deleteEntryModal = deleteBookmarkEntryModal()

            deleteEntryModal?.querySelector(".confirm-element-delete")?.addEventListener("click", (): void => {
                sendBookmarksApiRequest(action, advertId)
                $(deleteEntryModal).modal("hide")
            })
        }

        $(deleteEntryModal).modal("show")
        return
    }

    await sendBookmarksApiRequest(action, advertId)
}

export async function sendBookmarksApiRequest(action: string, advertId: string): Promise<void> {
    const response = await fetch(apiEndpoint, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: new URLSearchParams({
            AuftragsID: advertId,
            CanonicalID: canonicalId,
            Action: action,
            ResponseSpec: "simple",
        }),
    })

    if (action === "insert") {
        if (response.status !== 200) {
            return
        }

        return updateBookmarkStateAfterInsertion(await response.json())
    }
    if (action === "remove") {
        return updateBookmarkStateAfterRemoval(response)
    }
}

export async function updateBookmarkStateAfterInsertion(responseData: BookmarksApiResponse): Promise<void> {
    if (responseData.status === false) {
        return
    }

    if (responseData.status === "full") {
        handleBookmarkFullState(responseData.countMax || responseData.count)
        return
    }

    siteConfig.merkzettelIds.push(bookmarkId)

    displayUpdateInfo("Die Anzeige wurde erfolgreich zum Merkzettel hinzugefügt.")

    updateBookmarkButtonStates()
    updateBookmarkCount(responseData.count)
}

async function updateBookmarkStateAfterRemoval(response: Response): Promise<void> {
    if (response.status !== 200) {
        return
    }

    const responseData = (await response.json()) as BookmarksApiResponse
    if (responseData.status === false) {
        return
    }

    const idIndex: number = siteConfig.merkzettelIds.indexOf(bookmarkId)
    if (idIndex >= 0) {
        siteConfig.merkzettelIds.splice(idIndex, 1)
    }

    const dataIndex: number = siteConfig.merkzettelIdsWithData.indexOf(bookmarkId)
    if (dataIndex >= 0) {
        siteConfig.merkzettelIdsWithData.splice(dataIndex, 1)
    }

    displayUpdateInfo("Die Anzeige wurde erfolgreich aus dem Merkzettel entfernt.")

    updateBookmarkButtonStates()
    updateBookmarkCount(responseData.count)
    bookmarkTags.clearTagAssignments()
}

function updateBookmarkCount(newCount: number): void {
    const counter: HTMLElement | null = counterElement()
    if (counter === null) {
        return
    }

    if (newCount === 0) {
        counter.closest("a.has-items")?.classList.remove("has-items")
    } else {
        counter.closest("a:not(.has-items)")?.classList.add("has-items")
    }

    counter.innerText = newCount.toString()
}

function updateBookmarkButtons(): void {
    bookmarkButtonList = bookmarkButtons()
}

function handleBookmarkFullState(count: number): void {
    InfoPanel.displayMessage(
        `Dein Merkzettel ist voll. Du kannst maximal ${count} Anzeigen im Merkzettel speichern.`,
        InfoPanel.Type.Error,
        2 * 1000, // expects Milliseconds
    )
}

export function displayUpdateInfo(text: string): void {
    InfoPanel.displayMessage(
        text,
        InfoPanel.Type.Success,
        2 * 1000, // expects Milliseconds
    )
}
